import { StrokedText } from "@/components/ui/text/stroked-text";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { PencilIcon } from "@/icons";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { PokeTeam } from "@/types";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";
import { useState } from "react";
import { PokemonHoverDetails } from "./pokemon-hover-details";
import cn from "classnames";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { isXsScreen } from "@/utils/screen-size-check";

type TeamPokemon = PokeTeam["pokemons"][number];

interface TeamCardProps {
  team: PokeTeam;
  onClick?: (team: PokeTeam) => void;
  teamName?: string;
  handleNameChange?: (name: string) => void;
  onSetMainTeam?: (teamId: number) => void;
  mainTeamLoadingId?: number | null;
  handlePokemonChange?: () => void;
  slots?: (TeamPokemon | null)[];
  onSlotsChange?: (slots: (TeamPokemon | null)[]) => void;
}

const SortablePokemonSlot = ({
  id,
  pokemon,
  onHover,
  isHovered,
  disabled,
  index,
}: {
  id: string;
  pokemon: TeamPokemon | null;
  index: number;
  onHover: (pokemon: TeamPokemon | null) => void;
  isHovered: boolean;
  disabled?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !!disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "flex flex-col items-center border-b-8 transition justify-center w-full h-[64px] min-h-[64px] max-h-[64px] relative touch-none shrink-0",
        {
          "bg-pk-orange-4/80 border-b-pk-orange-4": isHovered,
          "bg-pk-blue-3/50 border-b-pk-blue-2": !isHovered,
          "cursor-grab active:cursor-grabbing": !disabled,
          "opacity-0": isDragging, // Hide the original item while dragging
          "hover:scale-105": !isDragging && !disabled,
        },
      )}
      onMouseEnter={() => onHover(pokemon)}
      onMouseLeave={() => onHover(null)}
    >
      {pokemon && (
        <>
          <div className="relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <PokemonSprite
                pokemon={pokemon}
                className="w-16 h-16"
                style={{ scale: isXsScreen() ? 1.35 : 1.75 }}
                ignoreScale={true}
              />
            </div>
          </div>
          <StrokedText
            strokeWidth={8}
            fontSize={12}
            className="z-10 absolute bottom-0 left-1/2 -translate-x-1/2"
          >
            {`#${pokemon.display_id.slice(0, 6)}`}
          </StrokedText>
        </>
      )}
    </div>
  );
};

/**
 * Displays a Pokemon card in the team builder showing sprite, talents, skills, and held item
 */
export const TeamCard = ({
  team,
  onClick,
  handleNameChange,
  teamName,
  onSetMainTeam,
  mainTeamLoadingId,
  handlePokemonChange,
  slots,
  onSlotsChange,
}: TeamCardProps) => {
  const [hoveredPokemon, setHoveredPokemon] = useState<TeamPokemon | null>(
    null,
  );
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200, // Click and hold for 200ms
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const items = Array.from({ length: 8 }).map((_, index) => {
    // Order pokemons based on team order field
    const orderedPokemons = Array.from({ length: 8 })
      .map((_, index) => {
        const pokeId = team.order?.[index as keyof typeof team.order];
        if (!pokeId) return null;

        const poke = team.pokemons.find((pokemon) => pokemon.id === pokeId);
        return poke || null;
      })
      .filter((pokemon) => pokemon !== null);

    const pokemon = slots ? slots[index] : orderedPokemons[index];
    return {
      id: pokemon ? `p-${pokemon.id}` : `empty-${index}`,
      pokemon: pokemon || null,
      index,
    };
  });

  const isDraggable = !!onSlotsChange && !!slots;

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);

    if (!over || !onSlotsChange || !slots) return;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newSlots = arrayMove(slots, oldIndex, newIndex);
        onSlotsChange(newSlots);
      }
    }
  }

  const activeItem = items.find((item) => item.id === activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="relative group">
        <StandardFrame
          rounded
          className={`flex flex-col gap-3 relative transition overflow-hidden shadow-cartoon ${
            onClick ? "cursor-pointer  active:translate-y-[2px]" : ""
          }`}
          variant="blue"
          onClick={() => onClick?.(team)}
        >
          {/* Subtle texture + shine sweep (DS-ish polish) */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.10] z-[1]"
            style={{
              backgroundImage: "url('/assets/app-ui/textre.png')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "repeat",
            }}
          />
          <div className="absolute transition-all duration-700 group-hover:translate-x-[2060%] translate-x-[-260%] bg-pk-gray-5/10 z-[2] h-[200%] w-10 rotate-12 top-[-40%] left-0 pointer-events-none" />

          {/* Header bar */}
          <div className="flex items-center justify-between px-2 py-1 relative z-[5] overflow-hidden bg-gradient-to-b from-pk-blue-2 to-pk-blue-1 border-b-2 border-pk-blue-4/70">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-pk-blue-6/60 to-transparent" />
            <div className="absolute inset-0 opacity-[0.10] bg-[url('/assets/app-ui/pattern-dots.png')] bg-repeat z-[1]" />

            {/* Decorative corners */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-pk-blue-6/70 rounded-tl-sm" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-pk-blue-6/70 rounded-tr-sm" />

            {handleNameChange ? (
              <div className="flex flex-row items-center gap-2 p-1 w-full">
                <div className="flex flex-row items-center gap-1 bg-pk-night-1/35 rounded-sm px-2 py-1 border-2 border-pk-blue-4/80 flex-1 z-[2]">
                  <PencilIcon
                    width={16}
                    height={16}
                    className="text-pk-yellow-3 flex-shrink-0"
                  />
                  <input
                    type="text"
                    value={teamName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Enter team name..."
                    className="w-full text-xl bg-transparent outline-none border-none text-pk-white placeholder-pk-gray-4"
                    maxLength={50}
                    autoFocus
                  />
                </div>
              </div>
            ) : (
              <div className="z-[2] px-2 py-[2px] bg-pk-night-1/25 border-2 border-pk-blue-4/70 rounded-sm shadow-cartoon-sm">
                <StrokedText strokeWidth={10} fontSize={16} className="z-10">
                  {team.name}
                </StrokedText>
              </div>
            )}

            {onSetMainTeam && (
              <div
                className="z-10 flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                {mainTeamLoadingId === team.id ? (
                  <span className="inline-block w-5 h-5 border-2 border-pk-blue-5 border-t-pk-gray-5 rounded-full animate-spin" />
                ) : (
                  <input
                    type="checkbox"
                    checked={!!team.isMainTeam}
                    onChange={() => {
                      if (!team.isMainTeam) onSetMainTeam(team.id);
                    }}
                    className="w-5 h-5 rounded border-2 border-pk-blue-4 bg-pk-night-1 accent-pk-blue-4 cursor-pointer"
                    title={team.isMainTeam ? "Main team" : "Set as main team"}
                  />
                )}
                <span className="text-xs text-pk-gray-5 hidden xs:inline">
                  Main
                </span>
              </div>
            )}
          </div>

          {/* Pokemon List */}
          <SortableContext
            items={items.map((i) => i.id)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-4 gap-0 gap-y-8 auto-rows-[64px] rounded-sm z-[5]">
              {items.map((item) => (
                <SortablePokemonSlot
                  key={item.id}
                  id={item.id}
                  pokemon={item.pokemon}
                  index={item.index}
                  onHover={setHoveredPokemon}
                  isHovered={
                    hoveredPokemon?.id === item.pokemon?.id && !!item.pokemon
                  }
                  disabled={!isDraggable}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay adjustScale={true}>
            {activeItem ? (
              <div
                className={cn(
                  "flex flex-col items-center rounded-sm border-b-8 transition justify-center w-full h-[64px] relative cursor-grabbing scale-105 shadow-2xl z-50",
                  {
                    "bg-pk-orange-4/80 border-b-pk-orange-5":
                      !!activeItem.pokemon,
                    "bg-pk-blue-3/50 border-b-pk-blue-2": !activeItem.pokemon,
                  },
                )}
              >
                {activeItem.pokemon && (
                  <>
                    <PokemonSprite
                      pokemon={activeItem.pokemon}
                      className="w-16 h-16"
                      style={{ scale: isXsScreen() ? 1.75 : 2.5 }}
                    />
                    <StrokedText strokeWidth={8} fontSize={12} className="z-10">
                      {`#${
                        activeItem.pokemon.display_id.length > 9
                          ? activeItem.pokemon.display_id.slice(0, 9)
                          : activeItem.pokemon.display_id
                      }`}
                    </StrokedText>
                  </>
                )}
              </div>
            ) : null}
          </DragOverlay>

          {/* Team Stats */}

          {hoveredPokemon ? (
            <div className="w-full h-auto transition-all duration-300">
              <PokemonHoverDetails pokemon={hoveredPokemon} className="-mt-6" />
            </div>
          ) : (
            <></>
          )}

          {/* Actions */}
          {handlePokemonChange && (
            <div className="flex flex-row items-center justify-between mt-8 z-15">
              <SimpleButton variant="gray" onClick={handlePokemonChange}>
                Change Pokemons
              </SimpleButton>
            </div>
          )}
        </StandardFrame>
      </div>
      {isDraggable && (
        <div className=" bg-pk-blue-1 rounded-md text-start flex flex-col gap-2 p-2  text-pk-gray-5 text-sm ">
          <span className="text-xs xs:text-sm">
            Tap and hold a Pokémon to reorder the team
          </span>
          <span className="text-xs xs:text-sm">
            #1 is the first to enter a battle, then #2, and so on.
          </span>
        </div>
      )}
    </DndContext>
  );
};
